/**
 * Agency social scheduling (flag social.agency_scheduling): per granted client workspace, see the client's connected
 * Facebook / Instagram / LinkedIn accounts, compose a post (caption + media from the client's asset library), pick a time
 * and schedule it. The client still approves every post; the publisher re-checks that approval, so nothing is published
 * before it. Each post shows its status (awaiting approval, scheduled, published, failed with the reason).
 * Access is the client's active grant to this agency (server side; 404 otherwise). New social accounts are connected by
 * the client in their own portal (Integrations); this screen never runs OAuth.
 */
import './social.css';
import { useMemo, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import { Button, Heading, Panel, Text } from '@qm/ui';
import type { AgencyPostStatus } from '@qm/contracts/social';
import { useSurface } from '../kit/context';
import { useAction, useResource } from '../kit/resource';
import { Badge, Checkbox, DataTable, ErrorNote, Field, Loadable, Notice, Screen, When, type Tone } from '../kit/ui';
import { socialApi, type AgencyAssetView, type AgencyPostView, type AgencySocialAccountView, type BestTime, type ValidationResult } from '../api/social';
import { usePartnerApi } from './layout';

export const POST_STATUS_TONE: Record<AgencyPostStatus, Tone> = {
  awaiting_approval: 'warn', changes_requested: 'warn', rejected: 'bad', scheduled: 'accent', publishing: 'accent',
  published: 'good', failed: 'bad', not_approved: 'bad', cancelled: 'neutral',
};
const CANCELLABLE: readonly AgencyPostStatus[] = ['awaiting_approval', 'changes_requested', 'rejected', 'scheduled', 'not_approved'];
/** Error codes with their own sentence; anything else shows the generic one with the code. */
export const KNOWN_ERRORS = ['not_approved', 'credentials_unavailable', 'account_disconnected', 'media_required', 'media_aspect_ratio', 'unexpected_error'] as const;
export const CAPTION_MAX = 3000;

/** `datetime-local` value (local wall time) -> ISO UTC; null when empty or invalid. */
export function localToIso(v: string): string | null {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}
/** ISO -> `datetime-local` value in the browser's zone. */
export function isoToLocal(iso: string): string {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

export function useSocialApi() {
  const { http } = useSurface();
  return useMemo(() => socialApi(http), [http]);
}

export function SocialPage() {
  const { t } = useSurface();
  const api = usePartnerApi();
  const [sp, setSp] = useSearchParams();
  const client = sp.get('client') ?? '';
  const clients = useResource('clients:social', () => api.clients({ tab: 'active', limit: 100 }).then((e) => e.data));
  return (
    <Screen title={t('portal.partners.social.title')} subtitle={t('portal.partners.social.subtitle')} testId="agency-social">
      <Loadable resource={clients}>
        {(list) => (
          <>
            <Field label={t('portal.partners.social.client')}>
              {(id) => (
                <select id={id} value={client} onChange={(e) => setSp(e.target.value ? { client: e.target.value } : {})}>
                  <option value="">{t('portal.partners.social.chooseClient')}</option>
                  {list.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              )}
            </Field>
            {client ? <ClientSocial key={client} tenantId={client} /> : <Notice testId="social-choose-client">{t('portal.partners.social.chooseClientFirst')}</Notice>}
          </>
        )}
      </Loadable>
    </Screen>
  );
}

export function ClientSocial({ tenantId }: { tenantId: string }) {
  const api = useSocialApi();
  const accounts = useResource(`social:accounts:${tenantId}`, () => api.accounts(tenantId));
  const assets = useResource(`social:assets:${tenantId}`, () => api.assets(tenantId));
  const posts = useResource(`social:posts:${tenantId}`, () => api.posts(tenantId));
  return (
    <Loadable resource={accounts}>
      {(acc) => (
        <>
          <AccountsPanel accounts={acc.data} connectUrl={acc.meta?.connectUrl ?? null} />
          {acc.data.some((a) => a.status === 'connected') ? (
            <Loadable resource={assets}>
              {(list) => <ComposePanel tenantId={tenantId} accounts={acc.data.filter((a) => a.status === 'connected')} assets={list} onScheduled={posts.reload} />}
            </Loadable>
          ) : null}
          <Loadable resource={posts}>{(rows) => <PostsPanel tenantId={tenantId} rows={rows} onChange={posts.reload} />}</Loadable>
        </>
      )}
    </Loadable>
  );
}

function AccountsPanel({ accounts, connectUrl }: { accounts: AgencySocialAccountView[]; connectUrl: string | null }) {
  const { t } = useSurface();
  const connected = accounts.filter((a) => a.status === 'connected');
  return (
    <Panel data-testid="social-accounts">
      <Heading level={2}>{t('portal.partners.social.accounts')}</Heading>
      {accounts.length ? (
        <ul className="qx-social-list">
          {accounts.map((a) => (
            <li key={a.id} className="qx-toolbar" data-account={a.id}>
              <Badge tone="accent">{t(`portal.partners.social.provider.${a.provider}`)}</Badge>
              <span>{a.displayName}</span>
              <Badge tone={a.status === 'connected' ? 'good' : 'warn'} testId={`account-${a.status}`}>{t(`portal.partners.social.account.${a.status}`)}</Badge>
            </li>
          ))}
        </ul>
      ) : null}
      {connected.length === 0 ? <Notice tone="warn" testId="social-no-accounts">{t('portal.partners.social.noAccounts')}</Notice> : null}
      <Text tone="muted">
        {t('portal.partners.social.connectHint')}{' '}
        {connectUrl ? <a className="qx-link" href={connectUrl} target="_blank" rel="noopener noreferrer" data-testid="social-connect-link">{t('portal.partners.social.connectLink')}</a> : null}
      </Text>
    </Panel>
  );
}

function ComposePanel({ tenantId, accounts, assets, onScheduled }: { tenantId: string; accounts: AgencySocialAccountView[]; assets: AgencyAssetView[]; onScheduled: () => void }) {
  const { t, now } = useSurface();
  const api = useSocialApi();
  const [accountId, setAccountId] = useState(accounts[0]?.id ?? '');
  const [caption, setCaption] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [when, setWhen] = useState('');
  const [check, setCheck] = useState<ValidationResult | null>(null);
  const [done, setDone] = useState(false);
  const account = accounts.find((a) => a.id === accountId) ?? null;
  const best = useResource<BestTime[] | null>(`social:best:${tenantId}:${account?.id ?? ''}`, () => (account ? api.bestTimes(tenantId, account.id, now()) : Promise.resolve(null)));
  const scheduledAt = localToIso(when);
  const submit = useAction(async () => {
    if (!account || !scheduledAt) return null;
    const v = await api.validate(tenantId, { accountId: account.id, caption, assetIds: picked });
    setCheck(v);
    if (v && !v.ok) return null;
    return api.schedule(tenantId, { accountId: account.id, caption, assetIds: picked, scheduledAt });
  });
  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setDone(false);
    if (await submit.run()) { setCaption(''); setPicked([]); setWhen(''); setDone(true); onScheduled(); }
  };
  const valid = !!account && caption.trim().length > 0 && caption.length <= CAPTION_MAX && !!scheduledAt;
  return (
    <Panel data-testid="social-compose" className="qx-social-compose">
      <Heading level={2}>{t('portal.partners.social.compose')}</Heading>
      <form onSubmit={(e) => void onSubmit(e)} noValidate>
        <Field label={t('portal.partners.social.account')}>
          {(id) => (
            <select id={id} value={accountId} onChange={(e) => { setAccountId(e.target.value); setCheck(null); }}>
              {accounts.map((a) => <option key={a.id} value={a.id}>{`${t(`portal.partners.social.provider.${a.provider}`)} - ${a.displayName}`}</option>)}
            </select>
          )}
        </Field>
        <Field label={t('portal.partners.social.caption')} hint={t('portal.partners.social.captionCount', { n: caption.length, max: CAPTION_MAX })}>
          {(id, describedBy) => <textarea id={id} name="caption" rows={5} maxLength={CAPTION_MAX} value={caption} aria-describedby={describedBy} onChange={(e) => { setCaption(e.target.value); setCheck(null); }} />}
        </Field>
        <fieldset className="qx-social-media" data-testid="social-media">
          <legend>{t('portal.partners.social.media')}</legend>
          {assets.length === 0 ? <Text tone="muted">{t('portal.partners.social.noAssets')}</Text> : assets.map((a) => (
            <Checkbox key={a.id} name="asset" label={`${a.title} (${t(`portal.partners.social.kind.${a.kind}`)})`} checked={picked.includes(a.id)}
              onChange={(v) => { setPicked((p) => (v ? [...p, a.id].slice(0, 10) : p.filter((x) => x !== a.id))); setCheck(null); }} />
          ))}
          {account?.provider === 'instagram' && picked.length === 0 ? <Text tone="muted">{t('portal.partners.social.instagramNeedsMedia')}</Text> : null}
        </fieldset>
        <Field label={t('portal.partners.social.when')}>
          {(id) => <input id={id} type="datetime-local" name="scheduledAt" value={when} onChange={(e) => setWhen(e.target.value)} />}
        </Field>
        {best.status === 'ready' && best.data && best.data.length ? (
          <div className="qx-toolbar" data-testid="social-best-times">
            <Text tone="muted">{t('portal.partners.social.bestTimes')}</Text>
            {best.data.map((b) => <Button key={b.at} variant="ghost" onClick={() => setWhen(isoToLocal(b.at))}><When iso={b.at} /></Button>)}
          </div>
        ) : null}
        {check && check.issues.length ? (
          <Notice tone={check.ok ? 'warn' : 'bad'} testId="social-validation">
            <ul className="qx-social-issues">{check.issues.map((i) => <li key={`${i.severity}:${i.code}`}>{i.message ?? i.code}</li>)}</ul>
          </Notice>
        ) : null}
        <Text tone="muted">{t('portal.partners.social.approvalNote')}</Text>
        <Button type="submit" variant="primary" disabled={!valid || submit.pending}>{t('portal.partners.social.schedule')}</Button>
      </form>
      {done ? <Notice tone="good" testId="social-scheduled">{t('portal.partners.social.scheduled')}</Notice> : null}
      <ErrorNote error={submit.error} />
    </Panel>
  );
}

export function errorText(t: (k: string, p?: Record<string, string | number>) => string, e: AgencyPostView['error']): string | null {
  if (!e) return null;
  return (KNOWN_ERRORS as readonly string[]).includes(e.code) ? t(`portal.partners.social.error.${e.code}`) : t('portal.partners.social.error.other', { code: e.code });
}

function PostsPanel({ tenantId, rows, onChange }: { tenantId: string; rows: AgencyPostView[]; onChange: () => void }) {
  const { t } = useSurface();
  const api = useSocialApi();
  const cancel = useAction(async (id: string) => api.cancel(tenantId, id));
  return (
    <Panel data-testid="social-posts">
      <Heading level={2}>{t('portal.partners.social.posts')}</Heading>
      <DataTable rows={rows} rowKey={(p) => p.id} empty={t('portal.partners.social.noPosts')} columns={[
        { key: 'when', header: t('portal.partners.social.when'), render: (p) => <When iso={p.scheduledAt} /> },
        { key: 'account', header: t('portal.partners.social.account'), render: (p) => `${t(`portal.partners.social.provider.${p.provider}`)} - ${p.accountName}` },
        { key: 'caption', header: t('portal.partners.social.caption'), narrow: 'hide', render: (p) => (p.caption.length > 80 ? `${p.caption.slice(0, 79)}…` : p.caption) },
        {
          key: 'status', header: t('portal.partners.clients.col.status'), render: (p) => (
            <span data-post={p.id}>
              <Badge tone={POST_STATUS_TONE[p.status] ?? 'neutral'} testId={`post-${p.status}`}>{t(`portal.partners.social.status.${p.status}`)}</Badge>
              {errorText(t, p.error) ? <Text tone="muted">{errorText(t, p.error)}</Text> : null}
              {p.publishedUrl ? <a className="qx-link" href={p.publishedUrl} target="_blank" rel="noopener noreferrer">{t('portal.partners.social.view')}</a> : null}
            </span>
          ),
        },
        { key: 'act', header: '', render: (p) => (CANCELLABLE.includes(p.status) ? <Button variant="ghost" disabled={cancel.pending} onClick={async () => { if (await cancel.run(p.id)) onChange(); }}>{t('portal.partners.social.cancel')}</Button> : null) },
      ]} />
      <ErrorNote error={cancel.error} />
    </Panel>
  );
}
